import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Cancel as CancelIcon,
  LockOpen as LockOpen,
  Replay as ReplayIcon, // Add ReplayIcon import
  DeleteOutline as DeleteOutlineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import useCan from '../hooks/useCan';
import processService from '../services/processService';
import codexService from '../services/codexService';
import { createFileService } from '../services/file';
import { formatDate } from '../utils/dateUtils';
import CodexHeader from '../components/codex/CodexHeader';
import CodexAttributes from '../components/codex/CodexAttributes';
import { FileViewer } from '../components/file-viewer';
import { useCodexData } from '../hooks/useCodexData';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE } from '../constants/fileConstants';
import { useFileContent } from '../hooks/useFileContent';
import taskService, { taskActionErrorMessage } from '../services/taskService';
import { STATUS_CONFIG } from '../config/statusConfig';

/**
 * CodexDetail - Main page component for displaying and editing codex details
 *
 * This component orchestrates the display of codex information, attributes,
 * and file previews. It handles the main state management and coordinates
 * between different sections of the page with enhanced file caching.
 */
const CodexDetail = () => {
  const can = useCan();
  const { codexId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve taskId and fromQueueActivities from the navigation state
  const taskId = location.state?.taskId;
  const fromQueueActivities = location.state?.fromQueueActivities;

  // User permissions
  // RBAC Fase 3: il backend protegge le due azioni con permessi distinti, quindi qui
  // servono due variabili e non più un unico predicato isOperator().
  const canDeleteCodex = can('codex.delete');
  const canEditAttributes = can('attributes.update');
  // Il cestino dell'Anteprima: DELETE /codex-files/{id} chiede files.delete.
  const canDeleteFiles = can('files.delete');
  // Le pagine elaborate (AUTO-213): lo stesso permesso della pagina «Pagine elaborate».
  const canSeeProcessedPages = can('llm_usage.read');
  // L'ultima elaborazione del Codex: GET /codex-processes chiede processes_run.read.
  const canReadProcessRuns = can('processes_run.read');

  // Create file service instance using factory
  const fileService = useMemo(() =>
    createFileService('codex', { codexService, codexId }),
    [codexId]
  );

  // Custom hooks for data management with enhanced caching
  const { codex, loading: codexLoading, error: codexError, setCodex, updateCodex } = useCodexData(codexId); // Destructure setCodex
  const { fileContent, loading: fileLoading, error: fileError, loadFileContent } = useFileContent();

  // Local state
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [actionTaskId, setActionTaskId] = useState(null);
  // State for complete dialog
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeReason, setCompleteReason] = useState('');

  // State for reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // State for unclaim dialog
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);
  const [unclaimReason, setUnclaimReason] = useState('');
  const [unclaimError, setUnclaimError] = useState(null);

  // State for tracking unsaved changes
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // State for file viewer visibility toggle
  const [fileViewerVisible, setFileViewerVisible] = useState(true);

  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load the last process of the codex when codex data is available.
  // Non si leggono piu' i processi del tipo (GET /processes, processes.read): il risultato
  // non era usato, e il suo 403 per validator e viewer impediva anche questa lettura.
  useEffect(() => {
    const loadLastProcess = async () => {
      if (!codex?.id || !canReadProcessRuns) return;

      try {
        setSelectedProcess(await processService.getLastCodexProcessByCodexId(codex.id));
      } catch (err) {
        console.error('Error loading last process:', err);
      }
    };

    loadLastProcess();
  }, [codex?.id, canReadProcessRuns]);

  // Use navigation protection hook
  useEffect(() => {
    // After render, find any PDF iframe/object and disable their history manipulation
    const pdfElements = document.querySelectorAll('iframe[src*="application/pdf"], object[type="application/pdf"]');
    pdfElements.forEach(el => {
      try {
        if (el.contentWindow && el.contentWindow.history) {
          el.contentWindow.history.pushState = () => {};
          el.contentWindow.history.replaceState = () => {};
        }
      } catch (e) {
        // Cross-origin, ignore
      }
    });
  }, [codex?.files, selectedFileIndex]);

  // Handle file tab change
  const handleFileChange = useCallback((event, newValue) => {
    setSelectedFileIndex(newValue);
  }, []);

  // Navigation handlers
  const handleGoBack = useCallback(() => {
    navigate('/documenti');
  }, [navigate]);
  const handleNavigateToDocuments = useCallback(() => navigate('/documenti'), [navigate]);

  // Determine content to render based on state
  const renderContent = () => {
    // Handle loading state
    if (codexLoading && !codex) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Handle error state
    if (codexError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{codexError}</Alert>
          {renderBreadcrumbs('Error')}
        </Box>
      );
    }

    // Handle not found state
    if (!codex) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>Codex not found</Alert>
          {renderBreadcrumbs('Not Found')}
        </Box>
      );
    }

    // Render main content
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          pt: 2,
          pb: 2,
          minHeight: 0
        }}
      >
        <Box sx={{ mb: 2 }}>
          {renderBreadcrumbs(codex?.name || codex?.code || `Codex ${codexId}`)}
        </Box>
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          {renderMainContent()}
        </Box>
      </Box>
    );
  };

  // Functions for Complete Task Dialog
  const handleOpenCompleteDialog = () => {
    if (unsavedChanges) {
      alert('Ci sono modifiche non salvate negli attributi. Salva le modifiche prima di completare il task.');
      return;
    }
    
    setActionTaskId(taskId);
    setCompleteReason('');
    setCompleteDialogOpen(true);
  };

  const handleCloseCompleteDialog = () => {
    setCompleteDialogOpen(false);
    setCompleteReason('');
    setActionTaskId(null);
  };

  const handleConfirmComplete = async () => {
    if (!actionTaskId || !completeReason.trim()) {
      setError('Please provide a reason to complete the task.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await taskService.completeTask(actionTaskId, {}, completeReason);      
      handleCloseCompleteDialog();
      // Force refresh by adding a timestamp parameter to ensure data reload
      navigate(`/codex/${codexId}?refresh=${Date.now()}`);
    } catch (err) {
      console.error('Error completing task:', err);
      setError('Failed to complete task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryProcess = async () => {
    try {
      setLoading(true);
      setError(null);
      await processService.restartCodexProcess(codexId, selectedProcess.processId, {}); // Use the new API and pass codexId and selectedProcess.id
      const refreshedCodex = await codexService.getById(codexId); // Call getById directly
      setCodex(refreshedCodex); // Update the codex state with the refreshed data
      handleCloseCompleteDialog(); // Close any open dialogs
      navigate(`/codex/${codexId}`); // Navigate to the same page to force a reload if needed
    } catch (err) {
      console.error('Error retrying process:', err);
      setError('Failed to retry process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

    // Functions for Reject Task Dialog
  const handleOpenRejectDialog = () => {
    setActionTaskId(taskId);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectReason('');
    setActionTaskId(null);
  };

  const handleConfirmReject = async () => {
    if (!actionTaskId || !rejectReason.trim()) {
      setError('Please provide a reason to reject the task.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await taskService.rejectTask(actionTaskId, {}, rejectReason);
      handleCloseRejectDialog();
      navigate(`/codex/${codexId}`);
    } catch (err) {
      setError('Failed to reject task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Functions for Unclaim Task Dialog
  const handleOpenUnclaimDialog = () => {
    setActionTaskId(taskId);
    setUnclaimReason('');
    setUnclaimError(null);
    setUnclaimDialogOpen(true);
  };

  const handleCloseUnclaimDialog = () => {
    setUnclaimDialogOpen(false);
    setUnclaimReason('');
    setActionTaskId(null);
  };

  const handleConfirmUnclaim = async () => {
    if (!actionTaskId || !unclaimReason.trim()) return;
    try {
      setLoading(true);
      setUnclaimError(null);
      await taskService.unclaimTask(actionTaskId, unclaimReason);
      handleCloseUnclaimDialog();
      navigate(`/codex/${codexId}`);
    } catch (err) {
      console.error('Error unclaiming task:', err);
      setUnclaimError(taskActionErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) {
      return;
    }
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) {
      return;
    }
    try {
      setIsDeleting(true);
      setError(null);
      await codexService.delete(codexId);
      setDeleteDialogOpen(false);
      navigate('/documenti');
    } catch (err) {
      console.error('Error deleting codex:', err);
      setError('Impossibile eliminare il Codex. Riprova più tardi.');
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Render breadcrumbs navigation
  const renderBreadcrumbs = (currentPage) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <IconButton
        onClick={handleGoBack}
        data-testid="back-button"
        sx={{
          mr: 1,
          zIndex: 10,
          position: 'relative',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        <Link color="inherit" onClick={handleNavigateToDocuments} sx={{ cursor: 'pointer' }}>
          Documenti
        </Link>
        <Typography color="text.primary">{currentPage}</Typography>
      </Breadcrumbs>
      {/* Right side: Action Buttons (conditionally rendered) - positioned using auto margin and flex */}
      {(() => {
        const actionButtons = [];
        const queueActionsActive = Boolean(taskId && fromQueueActivities);

        if (queueActionsActive) {
          actionButtons.push(
            <Button
              key="complete"
              variant="contained"
              color="success"
              onClick={handleOpenCompleteDialog}
              startIcon={<CheckCircleOutlineIcon />}
            >
              Completa e Valida
            </Button>
          );
          actionButtons.push(
            <Button
              key="reject"
              variant="contained"
              color="error"
              onClick={handleOpenRejectDialog}
              startIcon={<CancelIcon />}
            >
              Rifiuta
            </Button>
          );
          actionButtons.push(
            <Button
              key="unclaim"
              variant="contained"
              color="warning"
              onClick={handleOpenUnclaimDialog}
              startIcon={<LockOpen />}
            >
              Rilascia Task
            </Button>
          );
        } else {
          if (codex?.status === 'FAILED' && !taskId) {
            actionButtons.push(
              <Button
                key="retry"
                variant="contained"
                color="warning"
                onClick={handleRetryProcess}
                startIcon={<ReplayIcon />}
              >
                Riprova Processo
              </Button>
            );
          }

          if (!fromQueueActivities && canDeleteCodex) {
            actionButtons.push(
              <Button
                key="delete"
                variant="contained"
                color="error"
                onClick={handleOpenDeleteDialog}
                startIcon={<DeleteOutlineIcon />}
                disabled={isDeleting}
              >
                Elimina
              </Button>
            );
          }
        }

        if (actionButtons.length === 0) {
          return null;
        }

        return (
          <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
            {actionButtons}
          </Box>
        );
      })()}
    </Box>
  );

  // Render the main content area (attributes and file preview)
  const renderMainContent = () => {
    const hasFiles = Boolean(codex?.files?.length);
    const previewVisible = hasFiles && fileViewerVisible;
    const availableHeight = 'calc(100vh - 220px)';

    return (
      <>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            gap: 0,
            width: '100%',
            minHeight: 0,
            maxWidth: '100%',
            height: availableHeight,
            maxHeight: availableHeight,
            position: 'relative',
          }}
        >
          {/* File Viewer Column */}
          <Box
            sx={{
              flexBasis: fileViewerVisible ? '33.333%' : '0%',
              maxWidth: fileViewerVisible ? '33.333%' : '0%',
              flexShrink: 0,
              transition: 'flex-basis 0.3s ease, max-width 0.3s ease, opacity 0.3s ease',
              opacity: fileViewerVisible ? 1 : 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 1,
              minHeight: 0,
              height: '100%',
              maxHeight: '100%',
              position: 'relative',
            }}
          >
            {fileViewerVisible && (
              <FileViewer
                files={codex.files}
                selectedFileIndex={selectedFileIndex}
                readOnly={false}
                showPreview={true}
                onFileChange={handleFileChange}
                fileService={fileService}
                enableManagement={true}
                canDelete={canDeleteFiles}
                maxFileSize={MAX_FILE_SIZE}
                allowedMimeTypes={ALLOWED_MIME_TYPES}
                title="Anteprima"
                sx={{ flexGrow: 1, minHeight: 0, height: '100%', maxHeight: '100%', overflow: 'hidden' }}
                enableInteractivePreview
              />
            )}
          </Box>

          {/* Drawer Toggle Button */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: fileViewerVisible ? 'calc(33.333% - 1px)' : '-1px',
              transform: 'translateY(-50%)',
              zIndex: 5,
              backgroundColor: 'background.paper',
              borderRadius: '0 4px 4px 0',
              boxShadow: 1,
              transition: 'left 0.3s ease, width 0.3s ease',
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: 'none',
              width: '4px',
              height: '60px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                width: '32px',
                boxShadow: 2,
                '& .drawer-icon': {
                  opacity: 1,
                },
              },
            }}
            onClick={() => setFileViewerVisible(!fileViewerVisible)}
          >
            {/* Icon that appears on hover */}
            <Box
              className="drawer-icon"
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {fileViewerVisible ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </Box>
          </Box>

          {/* Attributes Column */}
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: previewVisible ? '66.667%' : '100%',
              maxWidth: previewVisible ? '66.667%' : '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: 0,
              minHeight: 0,
              transition: 'flex-basis 0.3s ease, max-width 0.3s ease',
              overflow: 'hidden',
              height: '100%',
              maxHeight: '100%',
              ml: fileViewerVisible ? 0 : 0,
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <CodexHeader
                codex={codex}
                statusMap={STATUS_CONFIG.CODEX}
                formatDate={formatDate}
                selectedProcess={selectedProcess}
                processedPages={codex.processedPages}
                showProcessedPages={canSeeProcessedPages}
              />
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
              <CodexAttributes
                codex={codex}
                codexId={codexId}
                canEdit={canEditAttributes}
                onUpdate={updateCodex}
                unsavedChanges={unsavedChanges}
                setUnsavedChanges={setUnsavedChanges}
              />
            </Box>
          </Box>
        </Box>

        <Dialog open={completeDialogOpen} onClose={handleCloseCompleteDialog}>
          <DialogTitle>Completa e Valida</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Per favore, inserisci una nota per il completamento del task {codex?.name || ''}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="completeReason"
              label="Nota di completamento"
              type="text"
              fullWidth
              variant="outlined"
              value={completeReason}
              onChange={(e) => setCompleteReason(e.target.value)}
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCompleteDialog} color="primary">
              Annulla
            </Button>
            <Button onClick={handleConfirmComplete} color="primary" variant="contained">
              Completa Task
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog}>
          <DialogTitle>Rifiuta Task</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Per favore, inserisci una nota per il rifiuto del task {codex?.name || ''}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="rejectReason"
              label="Nota di rifiuto"
              type="text"
              fullWidth
              variant="outlined"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog} color="primary">
              Annulla
            </Button>
            <Button onClick={handleConfirmReject} color="primary" variant="contained">
              Rifiuta Task
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={unclaimDialogOpen} onClose={handleCloseUnclaimDialog}>
          <DialogTitle>Rilascia Task</DialogTitle>
          <DialogContent>
            {unclaimError && <Alert severity="error" sx={{ mb: 2 }}>{unclaimError}</Alert>}
            <DialogContentText sx={{ mb: 2 }}>
              Per favore, inserisci una nota per il rilascio del task {codex?.name || ''}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="unclaimReason"
              label="Nota di rilascio"
              required
              type="text"
              fullWidth
              variant="outlined"
              value={unclaimReason}
              onChange={(e) => setUnclaimReason(e.target.value)}
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUnclaimDialog} color="primary">
              Annulla
            </Button>
            <Button onClick={handleConfirmUnclaim} color="primary" variant="contained" disabled={loading || !unclaimReason.trim()}>
              Rilascia Task
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Elimina Codex</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Sei sicuro di voler eliminare il Codex {codex?.name || codex?.code || ''}? Questa azione non può essere annullata.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary" disabled={isDeleting}>
              Annulla
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              Elimina
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };


  return (
    <Box
      sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      onKeyDown={(e) => {
        // Prevent any keyboard shortcuts from interfering with navigation
        if (e.key === 'Escape' || (e.ctrlKey && e.key === '[')) {
          e.preventDefault();
          handleGoBack(e);
        }
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default CodexDetail;
